import React, { useMemo, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApiGet } from '../../utils/httpClientTs';
import { getElfPayPalActivityDetail } from '../../endpoints/financeElfPayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const ElfPaymentsPayPalActivityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useApiGet(
    ['finance-elf-payments-paypal-activity-detail', id],
    () => getElfPayPalActivityDetail(id!),
    { enabled: !!id, retry: 1 }
  );

  const rows = useMemo(() => {
    if (!data) return [];
    const member: ReactNode = data.matched_user_id ? (
      <Link
        component={RouterLink}
        to={`/finance/elf-loans/${data.matched_user_id}`}
      >
        {data.payer_label || data.matched_user_id}
      </Link>
    ) : (
      data.payer_label || '—'
    );
    return [
      ['Transaction ID', data.paypal_transaction_id],
      ['Source', data.source_kind],
      ['Amount', `${fmtMoney(data.amount)} ${data.currency_code || ''}`.trim()],
      ['Status', data.status || '—'],
      ['Result code', data.result_code || '—'],
      ['Response', data.response_msg || data.result_code_msg || '—'],
      ['Description', data.description || '—'],
      ['Transaction type', data.transaction_type || '—'],
      ['Tender type', data.tender_type || '—'],
      [
        'Transaction time',
        data.transaction_time
          ? new Date(data.transaction_time).toLocaleString()
          : '—',
      ],
      [
        'Report date',
        data.report_date ? new Date(data.report_date).toLocaleString() : '—',
      ],
      ['Schedule', data.is_recurring ? 'Recurring' : 'One-time'],
      ['Profile ID', data.profile_id || '—'],
      ['Customer ref', data.customer_ref || '—'],
      ['Merchant ID', data.merchant_id || '—'],
      ['Comment 1', data.comment_1 || '—'],
      ['Comment 2', data.comment_2 || '—'],
      ['Account last4', data.account_last4 || '—'],
      ['Routing last4', data.routing_last4 || '—'],
      ['Auth code', data.authcode || '—'],
      ['Expires', data.expires || '—'],
      ['AVS street', data.avs_street_match || '—'],
      ['AVS zip', data.avs_zip_match || '—'],
      ['Client IP', data.client_ip || '—'],
      ['Member', member],
      ['iMIS', data.imis_id || '—'],
      ['Report run', data.report_run_id || '—'],
    ] as [string, ReactNode][];
  }, [data]);

  const rawJson = useMemo(() => {
    if (!data?.raw_row_json) return '';
    try {
      return JSON.stringify(data.raw_row_json, null, 2);
    } catch {
      return String(data.raw_row_json);
    }
  }, [data]);

  if (!id) {
    return <Typography>Missing activity id.</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/finance/elf-payments/paypal')}
        sx={{ mb: 2 }}
      >
        Back to PayPal
      </Button>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography>Loading…</Typography>
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Failed to load activity.
        </Typography>
      )}

      {data && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" sx={{ wordBreak: 'break-all' }}>
              {data.paypal_transaction_id}
            </Typography>
            {data.has_error && (
              <Chip size="small" label="Error" color="error" />
            )}
            <Chip
              size="small"
              label={data.is_recurring ? 'Recurring' : 'One-time'}
              variant="outlined"
            />
          </Box>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableBody>
                {rows.map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        width: '28%',
                        fontWeight: 600,
                        verticalAlign: 'top',
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Raw row JSON
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              overflow: 'auto',
              maxHeight: 480,
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              component="pre"
              variant="body2"
              sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
            >
              {rawJson || '—'}
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ElfPaymentsPayPalActivityDetailPage;
